import * as uuid from 'uuid';

import {
	type ChartPayload,
	ChartAxisType,
	ChartOrientation,
	ChartSeriesOrientation,
	ChartType,
	ChartXAxisType,
	ChartDateFormat,
	SlideLayoutEnum,
	CarouselTheme
} from '@soomo/lib/types/index';

import { THEME_COLORS } from '@soomo/lib/components/pageElements/Carousel/constants';

import {
	type GeoMapPayload,
	type FigurePayload,
	type SAQuestionPayload,
	type ResponseBoardPayload,
	type MCQuestionPayload,
	type HTMLPayload,
	type ArtifactPayload,
	type BuilderPayload,
	type PopupQuizPayload,
	type ExternalToolPayload,
	type PollPayload,
	type NestingInstructionPayload,
	type QuestionDeckPayload,
	type DecisionPayload,
	type VoiceRecordingPayload,
	type PageProgressPayload,
	type PageElement,
	type MCChoicePayload,
	type PollChoice,
	type TimelinePayload,
	type GoReactExternalToolPayload,
	type MCQuestionPoolPayload,
	type MCQuestion,
	type PollResultsPayload,
	type PollChoicePayload,
	type AiChatboxPayload,
	type EocCheckinChatbotPayload,
	type CarouselPayload,
	type CarouselSlidePayload,
	ElementType,
	FigurePosition,
	FigureSubType,
	FigureType,
	GeoMapType,
	HeaderStyle,
	ElementConfigName
} from '@editor2/types/pageElements';
import { validationsMock } from '@editor2/containers/PageElementsPage/components/Question/ValidationMock';
import { createPopupQuizConfigJson } from './popupQuizService';
import { DEPRECATED_ELEMENTS } from '@editor2/types/deprecatedElements';
import { ALLOWED_NESTING } from '@editor2/types/allowedNesting';
import { DOUBLE_ELEMENTS } from '@editor2/constants/doubleElements';
import { ALLOWED_IN_QUESTION_DECK } from './../constants/allowedInQuestionDeck';
import {
	getGeoMapInitColors,
	getGeoMapInitData,
	getGeoMapInitLabels,
	getGeoMapTooltipTemplate,
	getGeoMapInitMargins
} from './geoMapService';
import { type ElementConfig } from '@editor2/types/config';
import { type AiModel } from '@editor2/types/aiModel';
import { newChatboxConfig } from './aiModelService';

interface DefaultPayload {
	family_id: string;
	metadata: any;
	type: any;
}

// eslint-disable-next-line complexity
export const getNewElement = (
	elementType: string,
	elementConfig?: ElementConfig,
	aiModels?: AiModel[]
) => {
	const elementConfigType = elementConfig?.type || elementType;
	const elementEntity: DefaultPayload = {
		family_id: uuid.v4(),
		metadata: {},
		type: ElementType[elementConfigType]
	};

	let element:
		| SAQuestionPayload
		| ResponseBoardPayload
		| MCQuestionPayload
		| MCChoicePayload
		| MCQuestionPoolPayload
		| PollPayload
		| PollResultsPayload
		| FigurePayload
		| ChartPayload
		| HTMLPayload
		| ArtifactPayload
		| NestingInstructionPayload
		| QuestionDeckPayload
		| AiChatboxPayload
		| EocCheckinChatbotPayload
		| CarouselPayload
		| CarouselSlidePayload
		| DefaultPayload;

	switch (ElementType[elementConfigType]) {
		case ElementType.SAQuestion: {
			const saQuestion: SAQuestionPayload = {
				...elementEntity,
				body: '',
				reference_answer: '',
				editable: null,
				validations: validationsMock,
				disabled: null,
				auto_save: null
			};
			element = saQuestion;
			break;
		}

		case ElementType.YourTurn: {
			const responseBoard: ResponseBoardPayload = {
				...elementEntity,
				editable: null,
				auto_save: null,
				validations: validationsMock,
				questions: [
					{
						family_id: uuid.v4(),
						body: '',
						metadata: {},
						type: ElementType.SAQuestion,
						reference_answer: '',
						disabled: null
					}
				]
			};
			element = responseBoard;
			break;
		}

		case ElementType.MCQuestion: {
			const choices = [...new Array(3)]
				.map(() => getNewElement('MCChoice') as MCChoicePayload)
				.map((choice, index) => (index === 0 ? { ...choice, is_correct: true } : choice));

			const mcQuestion: MCQuestionPayload = {
				...elementEntity,
				body: '',
				disabled: null,
				choices
			};
			element = mcQuestion;
			break;
		}

		case ElementType.MCChoice: {
			const mcChoice: MCChoicePayload = {
				...elementEntity,
				is_correct: false,
				body: '',
				rejoinder: ''
			};
			element = mcChoice;
			break;
		}

		case ElementType.MCQuestionPool: {
			const mcQuestionPool: MCQuestionPoolPayload = {
				...elementEntity,
				questions: [...new Array(3)].map(() => getNewElement('MCQuestion') as MCQuestion)
			};
			element = mcQuestionPool;
			break;
		}

		case ElementType.Figure: {
			const figure: FigurePayload = {
				...elementEntity,
				figure_type: FigureType.figure,
				figure_position: FigurePosition.normal,
				figure_number: '',
				title: '',
				payload: '',
				description: '',
				caption: '',
				credits: '',
				transcript_url: '',
				figure_subtype: FigureSubType.generic,
				payload_metadata: {
					vzaar_shared_secret: '',
					requires_signing: false,
					offline: false,
					map_json_data_url: ''
				},
				instructions: '',
				citation: '',
				map_json_data: null,
				figure_backup_url: '',
				header_style: HeaderStyle.none,
				quote_author: '',
				quote_citation: '',
				quote_text: '',
				disable_expanded_view: false
			};
			element = figure;
			break;
		}

		case ElementType.GeoMap: {
			const geoMap: GeoMapPayload = {
				...elementEntity,
				geomap_type: GeoMapType.US,
				figure_number: '',
				title: '',

				data: getGeoMapInitData(GeoMapType.US),
				labels: getGeoMapInitLabels(),
				colors: getGeoMapInitColors(),
				tooltip: getGeoMapTooltipTemplate(GeoMapType.US),

				margins: getGeoMapInitMargins(),

				description: '',
				caption: '',
				credits: '',
				instructions: ''
			};
			element = geoMap;
			break;
		}

		case ElementType.Chart: {
			const chart: ChartPayload = {
				...elementEntity,
				figure_number: '',
				title: '',
				description: '',
				credit: '',
				data: [
					['Category', 'Value'],
					[1, 2]
				],
				chart_type: ChartType.bar,
				orientation: ChartOrientation.horizontal,
				series_orientation: ChartSeriesOrientation.grouped,
				colors: ['#ccff00'],
				x_axis_type: ChartXAxisType.date,
				date_format: ChartDateFormat['Auto-formatted'],
				axis_type: ChartAxisType.normal,
				margins: [null, null, null, null],
				show_labels: true,
				show_markers: true,
				fill_line: false,
				round_to: 0
			};
			element = chart;
			break;
		}

		case ElementType.HTMLText: {
			const text: HTMLPayload = { ...elementEntity, body: '' };
			element = text;
			break;
		}

		case ElementType.Poll: {
			const poll: PollPayload = {
				...elementEntity,
				body: '',
				choices: [...new Array(3)].map(() => getNewElement('PollChoice') as PollChoice),
				chart_type: 'bar',
				color_group_by: 'dataset',
				choice_ordering: 'popularity_desc',
				instructions: '',
				caption: '',
				description: '',
				credits: ''
			};
			element = poll;
			break;
		}

		case ElementType.PollChoice: {
			const pollChoice: PollChoicePayload = {
				...elementEntity,
				body: '',
				shortened_body: ''
			};
			element = pollChoice;
			break;
		}

		case ElementType.PollResults: {
			const pollResults: PollResultsPayload = {
				...elementEntity,
				poll_question_family_id: ''
			};
			element = pollResults;
			break;
		}

		case ElementType.ArtifactArticle:
		case ElementType.ArtifactAudio:
		case ElementType.ArtifactVideo:
		case ElementType.ArtifactDownload:
		case ElementType.ArtifactPdfDownload:
		case ElementType.ArtifactDocxDownload:
		case ElementType.ArtifactXlsxDownload: {
			const artifact: ArtifactPayload = {
				...elementEntity,
				type: ElementType.ArtifactArticle,
				title: '',
				artifact_url: '',
				embed_code: '',
				transcript_url: '',
				credits: '',
				citation: '',
				artifact_backup_url: ''
			};
			element = artifact;
			break;
		}

		case ElementType.Builder: {
			const builder: BuilderPayload = {
				...elementEntity,
				type: ElementType.Builder,
				config: '{}',
				element_definition_key: elementType
			};
			element = builder;
			break;
		}

		case ElementType.PopupQuiz: {
			const popupElement: PopupQuizPayload = {
				...elementEntity,
				type: ElementType.PopupQuiz,
				questions: []
			};
			const popupQuiz: PopupQuizPayload = {
				...popupElement,
				config: createPopupQuizConfigJson(popupElement)
			};

			element = popupQuiz;
			break;
		}

		case ElementType.ExternalTool: {
			if (elementConfig?.settings?.tool_implementation === 'ExternalTools::GoReactTool') {
				const goReactExternalTool: GoReactExternalToolPayload = {
					...elementEntity,
					type: ElementType.ExternalTool,
					tool_name: elementType,
					config: '{}',
					score_config: { scoreOptions: 'noPoints' },
					prompt_body: '',
					title: '',
					caption: '',
					credits: '',
					transcript_url: ''
				};
				element = goReactExternalTool;
			} else {
				const externalTool: ExternalToolPayload = {
					...elementEntity,
					type: ElementType.ExternalTool,
					tool_name: elementType,
					config: ''
				};
				element = externalTool;
			}
			break;
		}

		case ElementType.NestingInstruction: {
			const nestingInstruction: NestingInstructionPayload = {
				...elementEntity,
				type: ElementType.NestingInstruction,
				direction: 'in',
				container_classes: []
			};
			element = nestingInstruction;
			break;
		}

		case ElementType.QuestionDeck: {
			const questionDeck: QuestionDeckPayload = {
				...elementEntity,
				type: ElementType.QuestionDeck,
				direction: 'in'
			};
			element = questionDeck;
			break;
		}

		case ElementType.Decision: {
			const decision: DecisionPayload = {
				...elementEntity,
				type: ElementType.Decision,
				name: '',
				prompt: ''
			};
			element = decision;
			break;
		}

		case ElementType.VoiceRecording: {
			const voiceRecording: VoiceRecordingPayload = {
				...elementEntity,
				type: ElementType.VoiceRecording,
				autosubmit: false,
				phone: '',
				instructions:
					'<p>\n  Call the number below to leave a recording:</p>\n' +
					'<p style="padding-left:5rem">\n  Number:\n  <span style="font-weight: bold">{phone}</span>\n</p>\n' +
					'<p>\n  When prompted, enter the number below and begin recording:\n</p>\n' +
					'<p style="padding-left:5rem">\n  Unique ID:\n  <span style="font-weight: bold">{pin}</span>\n</p>\n' +
					'<p>\n  Press star (*) on your phone keypad when you’re done recording.\n</p>',
				column_name: '',
				filename_template: '',
				lms_name: '',
				autosubmit_button_label: ''
			};
			element = voiceRecording;
			break;
		}

		case ElementType.PageProgress: {
			const pageProgress: PageProgressPayload = {
				...elementEntity,
				type: ElementType.PageProgress,
				target_page_family_id: '',
				min_time_spent_estimation_threshold: '',
				active_prediction_text: '',
				passive_prediction_text: '',
				active_prediction_bottom_text: ''
			};
			element = pageProgress;
			break;
		}

		case ElementType.Timeline: {
			const timeline: TimelinePayload = {
				...elementEntity,
				type: ElementType.Timeline,
				data: {
					events: []
				},
				figure_number: '',
				title: '',
				description: '',
				caption: '',
				credits: '',
				instructions: '',
				touchInstructions: ''
			};
			element = timeline;
			break;
		}

		case ElementType.Slide: {
			const slide: CarouselSlidePayload = {
				...elementEntity,
				type: ElementType.Slide,
				content: {
					layout: SlideLayoutEnum.Text,
					text: ''
				}
			};
			element = slide;
			break;
		}

		case ElementType.Carousel: {
			const carousel: CarouselPayload = {
				...elementEntity,
				type: ElementType.Carousel,
				figure_number: '',
				title: '',
				caption: '',
				credits: '',
				slides: [],
				theme: CarouselTheme.Soomo,
				color: THEME_COLORS[CarouselTheme.Soomo][0]
			};
			element = carousel;
			break;
		}

		case ElementType.AiChatbox: {
			const aiChatbox: AiChatboxPayload = {
				...elementEntity,
				title: '',
				config: newChatboxConfig(aiModels)
			};
			element = aiChatbox;
			break;
		}

		case ElementType.EocCheckinChatbot: {
			const eocCheckinChatbot: EocCheckinChatbotPayload = {
				...elementEntity,
				key_concepts: []
			};
			element = eocCheckinChatbot;
			break;
		}

		default:
			element = elementEntity;
			break;
	}

	return element;
};

export const getElementPreview = (element: PageElement) => {
	switch (element.type) {
		case ElementType.HTMLText:
		case ElementType.Poll:
		case ElementType.MCQuestion:
		case ElementType.SAQuestion:
			return element.body.split('\n')[0].substr(0, 128);
		case ElementType.Chart:
			return element.title.substr(0, 128);
		case ElementType.YourTurn:
			return element.questions.length ? element.questions[0].body.substr(0, 128) : '';
		case ElementType.Figure:
			if (element.description) return element.description.substr(0, 128);
			if (element.caption) return element.caption.substr(0, 128);
			break;
		case ElementType.GeoMap:
			if (element.description) return element.description.substr(0, 128);
			if (element.caption) return element.caption.substr(0, 128);
			break;
		case ElementType.MCQuestionPool:
			return Object.entries(element.metadata)
				.map((row) => row.join(': '))
				.join('; ');
		case ElementType.EocCheckinChatbot: {
			const count = element.key_concepts?.length ?? 0;
			return `${count} key concept${count === 1 ? '' : 's'}`;
		}
		default:
			return '';
	}
};

export const copyElement = (element) => {
	switch (true) {
		case Array.isArray(element): {
			return element.map((item) => copyElement(item));
		}

		case typeof element === 'object': {
			if (element == null) return null; // typeof null === "object"

			let elementCopy = {};

			for (const key in element) {
				if (key === 'family_id') {
					elementCopy = { ...elementCopy, family_id: uuid.v4() };
				} else if (key !== 'id') {
					elementCopy = { ...elementCopy, [key]: copyElement(element[key]) };
				}
			}

			return elementCopy;
		}

		default:
			return element;
	}
};

const getElementTypeName = (element: PageElement) => {
	switch (element.type) {
		case ElementType.Builder:
			return element.element_definition_key;
		case ElementType.ExternalTool:
			return element.tool_name;
		default:
			return ElementConfigName[element.type];
	}
};

export const getElementHumanName = (config, pageType, element: PageElement) => {
	const elementTypeName = getElementTypeName(element);

	const elementHumanName =
		config.page_types[pageType]?.elements[elementTypeName]?.human_name ||
		config.element_types[elementTypeName]?.human_name;

	return elementHumanName;
};

export const getElementSubtype = (element: PageElement) => {
	switch (element.type) {
		case ElementType.Figure:
			return `[${element.figure_type}]`;

		case ElementType.GeoMap:
			return `[${element.geomap_type}]`;

		case ElementType.ArtifactArticle:
		case ElementType.ArtifactAudio:
		case ElementType.ArtifactVideo:
		case ElementType.ArtifactDownload:
		case ElementType.ArtifactPdfDownload:
		case ElementType.ArtifactDocxDownload:
		case ElementType.ArtifactXlsxDownload:
			return `[${element.type.split('::').pop()}]`;

		default:
			break;
	}
};

export const isDeprecated = (type: ElementType) => {
	return DEPRECATED_ELEMENTS.includes(type);
};

export const isAllowedInNesting = (type: ElementType) => {
	return ALLOWED_NESTING.includes(type);
};

export const isAllowedInQuestionDeck = (type: ElementType) => {
	return ALLOWED_IN_QUESTION_DECK.includes(type);
};

export const isDoubleElement = (type: ElementType) => {
	return DOUBLE_ELEMENTS.includes(type);
};
